import React, { useCallback, useState } from "react";
import { AlertTriangle, Check, Loader2, Pencil } from "lucide-react";
import { api } from "./api";
import { Modale, BoutonSombre } from "./ui";

/* Fusion de deux fiches élèves quand elles se contredisent.

   L'école le 09.10.2026 : « je l'ai fusionné, par contre le système a juste sélectionné
   une des dates et ne demande pas une correction ? ». La fusion gardait en silence les
   valeurs de la fiche gardée (nom, date de naissance, AVS…). Désormais, la console lit
   d'abord l'aperçu de la fusion (rien n'est écrit) ; s'il y a des contradictions, la
   fenêtre les propose en choix, la valeur de la fiche gardée cochée d'office. */

export const apercuFusion = (params) =>
  api(`/eleves/fusion/apercu?${new URLSearchParams(params).toString()}`);

const nomFiche = (f) => `${f?.prenom || ""} ${f?.nom || ""}`.trim();

/* « 1 stage(s), 2 cours d'essai » : ce qui suivra la fiche gardée. */
export const lignesDeplacees = (apercu) => (apercu?.a_deplacer || [])
  .map((l) => `${l.nb} ${l.libelle}`).join(", ");

function FenetreChoixFusion({ apercu, titre, sous_titre, annuler, valider, onFin }) {
  const { garde, absorbe, conflits } = apercu;
  // La valeur de la fiche gardée est proposée d'office : un clic sur « Fusionner » fait
  // exactement ce que faisait l'ancienne fusion, mais en connaissance de cause.
  const [choix, setChoix] = useState(
    () => Object.fromEntries(conflits.map((c) => [c.champ, c.garde])));
  const deplace = lignesDeplacees(apercu);
  return (
    <Modale large titre={titre} sous_titre={sous_titre} onFermer={() => onFin(null)}
      testid="modale-fusion-choix">
      <div className="rounded-xl px-4 py-3 flex gap-3 items-start"
        style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#B45309" }}>
        <AlertTriangle size={18} className="shrink-0 mt-0.5" />
        <div className="text-sm" data-testid="fusion-choix-texte">
          Les deux fiches ne disent pas la même chose. Choisis ce qui est juste : c'est ce
          que gardera la fiche de {nomFiche(garde)}.
        </div>
      </div>

      <div className="space-y-4 mt-4">
        {conflits.map((c) => (
          <fieldset key={c.champ} data-testid={`fusion-conflit-${c.champ}`}>
            <legend className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">
              {c.libelle}
            </legend>
            <div className="grid sm:grid-cols-2 gap-2 mt-1.5">
              {[["garde", garde], ["absorbe", absorbe]].map(([cote, fiche]) => {
                const actif = choix[c.champ] === c[cote];
                return (
                  <label key={cote} data-testid={`fusion-choix-${c.champ}-${cote}`}
                    className="flex gap-2.5 items-start rounded-xl px-3 py-2.5 cursor-pointer transition-colors"
                    style={{
                      border: `1px solid ${actif ? "var(--primary)" : "var(--border)"}`,
                      background: actif ? "var(--primary-tint)" : "#fff",
                    }}>
                    <input type="radio" name={`fusion-${c.champ}`} checked={actif}
                      onChange={() => setChoix({ ...choix, [c.champ]: c[cote] })}
                      className="mt-1 shrink-0" style={{ accentColor: "var(--primary)" }} />
                    <span className="min-w-0">
                      <span className="block font-semibold text-sm break-words">{c[`${cote}_affiche`]}</span>
                      <span className="block text-xs text-[var(--muted)]">
                        fiche {nomFiche(fiche)}, {fiche.provenance}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      <ul className="text-xs text-[var(--muted)] mt-4 space-y-1" data-testid="fusion-choix-points">
        <li>· Tout ce qui est rattaché à la fiche de {nomFiche(absorbe)} ({absorbe.provenance})
          passe sur celle de {nomFiche(garde)}{deplace ? ` : ${deplace}` : ""}.</li>
        {(apercu.completes || []).length > 0 && (
          <li>· Repris de l'autre fiche (vide sur celle qu'on garde) : {apercu.completes.join(", ")}.</li>
        )}
        <li>· La fiche en trop est archivée, jamais effacée — elle reste dans le filtre « Archivés ».</li>
        <li>· Aucune des deux valeurs n'est juste ? Fusionne, puis corrige avec « Modifier la fiche ».</li>
      </ul>

      <div className="flex flex-wrap gap-2 mt-5">
        <button className="btn-ghost" onClick={() => onFin(null)} data-testid="fusion-choix-annuler">
          {annuler || "Annuler"}
        </button>
        <BoutonSombre onClick={() => onFin(choix)} testid="fusion-choix-valider">
          {valider || "Fusionner"}
        </BoutonSombre>
      </div>
    </Modale>
  );
}

/* `choisir({ apercu, titre })` ouvre la fenêtre et rend une promesse : les valeurs
   choisies ({ champ: valeur }), ou null si l'école annule. `fenetre` est à placer dans
   l'écran qui l'utilise. */
export function useChoixFusion() {
  const [boite, setBoite] = useState(null);
  const choisir = useCallback((options) => new Promise((resoudre) => {
    setBoite({ ...options, resoudre });
  }), []);
  const fenetre = boite ? (
    <FenetreChoixFusion {...boite} onFin={(r) => { boite.resoudre(r); setBoite(null); }} />
  ) : null;
  return { choisir, fenetre };
}

const valeurLisible = (champ, v) => {
  const t = String(v ?? "").trim();
  if (champ === "date_naissance" && /^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10).split("-").reverse().join(".");
  return t || "vide";
};

/* Sur la fiche élève : ce qu'une fusion n'a pas tranché (fusion faite sans fenêtre de
   choix — « Fusionner les doublons évidents », ou avant cette fenêtre). Reste affiché
   jusqu'à « C'est bon », « Prendre … » ou une correction avec « Modifier la fiche ». */
export function AVerifierFusion({ eleve, onFait, onModifier }) {
  const [occupe, setOccupe] = useState("");
  const [erreur, setErreur] = useState("");
  const items = eleve?.fusion_a_verifier || [];
  if (!items.length) return null;

  const trancher = async (cle, corps) => {
    setOccupe(cle);
    setErreur("");
    try {
      await api(`/eleves/${encodeURIComponent(eleve.id)}/fusion/verifier`,
        { method: "POST", body: JSON.stringify(corps) });
      await onFait();
    } catch (e) {
      setErreur(String(e.message || e));
    }
    setOccupe("");
  };

  return (
    <div className="rounded-xl px-4 py-3 mt-4" data-testid="eleve-fusion-a-verifier"
      style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: "#B45309" }}>
        À vérifier après la fusion
      </div>
      <ul className="mt-2 space-y-2">
        {items.map((v, k) => {
          const cle = `${v.champ}-${k}`;
          return (
            <li key={cle} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`fusion-a-verifier-${v.champ}`}>
              <span className="flex-1 min-w-[220px]">
                <strong>À vérifier :</strong> {v.message}. La fiche dit
                {" "}<strong>{valeurLisible(v.champ, eleve[v.champ])}</strong>.
              </span>
              <button className="btn-ghost" disabled={!!occupe} data-testid="fusion-verifier-ok"
                onClick={() => trancher(cle, { champ: v.champ })}>
                {occupe === cle ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} C'est bon
              </button>
              <button className="btn-ghost" disabled={!!occupe} data-testid="fusion-verifier-prendre"
                onClick={() => trancher(cle, { champ: v.champ, valeur: v.autre })}>
                Prendre {v.autre_affiche || valeurLisible(v.champ, v.autre)}
              </button>
            </li>
          );
        })}
      </ul>
      <button className="btn-ghost mt-2" data-testid="fusion-verifier-modifier" onClick={onModifier}>
        <Pencil size={13} /> Autre valeur : modifier la fiche
      </button>
      {erreur && <p className="text-xs mt-2" style={{ color: "#B91C1C" }}>{erreur}</p>}
    </div>
  );
}
